import { jsPDF } from 'jspdf';
import { mediaanApplies } from '@mv/models';
import { buildBoorprofielData } from './boorprofiel-data.js';
import { drawScaledProfile } from './boorprofiel-pdf.js';
import { scaleForBlock } from './boorprofiel-layout.js';
import { loadCompanyOrBrandLogo, stampPageHeader, stampPageFooter as stampFooter } from './pdf-logo.js';

// ─── Geometry ───────────────────────────────────────────────────────────────
const PW = 210, PH = 297;
const ML = 16, MR = 16, MT = 20, MB = 16;
const CW = PW - ML - MR;

const PROFILE_W = 20;   // coloured profile strip
const DEPTH_W   = 12;   // depth tick column (right of profile)
const TEXT_X    = ML + PROFILE_W + DEPTH_W + 4;
const TEXT_W    = CW - PROFILE_W - DEPTH_W - 4;

const SCALE     = 0.50;  // mm per cm of depth (visual proportion)
const MIN_ROW_H = 9;

// ─── Colours ────────────────────────────────────────────────────────────────
const SOIL_HEX = {
  Z:'#E8D5A3', G:'#C8C4BE', K:'#9BAFC2', L:'#C8A87A',
  V:'#5A3E2B', S:'#8DBBD1', E:'#A8A8A8', W:'#AED6E8',
};

function hexRgb(h) {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h);
  return r ? [parseInt(r[1],16), parseInt(r[2],16), parseInt(r[3],16)] : [220,220,220];
}

function layerFill(l) {
  if (l.colorHex && l.colorHex !== '#E0E0E0') return hexRgb(l.colorHex);
  return hexRgb(SOIL_HEX[l.soilType] || '#DDDDDD');
}

// ─── Hatching — drawn after the fill, clipped within the rect ───────────────
function hatch(doc, type, x, y, w, h) {
  if (!type) return;
  if (type === 'Z') {
    // Fine offset stipple
    doc.setFillColor(145, 112, 55);
    const sp = 2.0, r = 0.22;
    for (let ry = y + sp/2; ry < y + h - 0.2; ry += sp)
      for (let rx = x + sp/2; rx < x + w - 0.2; rx += sp) {
        const ox = (Math.round(ry / sp) % 2) * (sp / 2);
        if (rx + ox < x + w - 0.2) doc.circle(rx + ox, ry, r, 'F');
      }
  } else if (type === 'K') {
    doc.setDrawColor(65, 95, 130); doc.setLineWidth(0.15);
    for (let ry = y + 1.8; ry < y + h - 0.2; ry += 1.9)
      doc.line(x + 0.6, ry, x + w - 0.6, ry);
  } else if (type === 'V') {
    doc.setDrawColor(48, 30, 14); doc.setLineWidth(0.14);
    for (let ry = y + 1.8; ry < y + h - 0.2; ry += 1.9) doc.line(x+0.6, ry, x+w-0.6, ry);
    for (let rx = x + 1.8; rx < x + w - 0.2; rx += 1.9) doc.line(rx, y+0.6, rx, y+h-0.6);
  } else if (type === 'G') {
    doc.setDrawColor(80, 78, 74); doc.setFillColor(198, 194, 188); doc.setLineWidth(0.16);
    const sp = 2.5;
    for (let ry = y + sp/2; ry < y + h - 0.3; ry += sp)
      for (let rx = x + sp/2; rx < x + w - 0.3; rx += sp) {
        const ox = (Math.round(ry / sp) % 2) * (sp / 2);
        if (rx + ox < x + w - 0.3) doc.circle(rx + ox, ry, 0.7, 'FD');
      }
  } else if (type === 'L') {
    // Leem — diagonal hatching
    doc.setDrawColor(155, 120, 75); doc.setLineWidth(0.14);
    for (let d = -h; d < w + h; d += 2.2) {
      const x1 = x + d, y1 = y, x2 = x + d + h, y2 = y + h;
      doc.line(Math.max(x, x1), x1 < x ? y + (x - x1) : y,
               Math.min(x+w, x2), x2 > x+w ? y + (x+w - x1) : y+h);
    }
  }
}

// ─── Text helpers ────────────────────────────────────────────────────────────
function tf(doc, size, weight, col) {
  doc.setFontSize(size);
  doc.setFont('helvetica', weight || 'normal');
  doc.setTextColor(...(col || [20, 20, 28]));
}

// Calculate how many mm of vertical space the text content of a layer needs
function textHeight(doc, layer) {
  const LINE = 3.4;
  let lines = 1; // soil name always 1 line
  if (layer.kleurLabel) lines += 1;
  if (layer.bzbLabels?.length)
    lines += doc.splitTextToSize(layer.bzbLabels.join(', '), TEXT_W).length;
  if (layer.opmerking)
    lines += doc.splitTextToSize(layer.opmerking, TEXT_W).length;
  return lines * LINE + 4; // 4mm top padding
}

// ─── Chrome ──────────────────────────────────────────────────────────────────
// Kop (met logo) en voet gedeeld met de boorprofielen-export, zie pdf-logo.js.
function stampHeader(doc, pm, logo) {
  const label = [pm.projectName || pm.name, pm.projectCode || pm.code].filter(Boolean).join('  ·  ');
  stampPageHeader(doc, { title: 'BODEMKUNDIG RAPPORT', label, logo });
}

// ─── Sub-section helpers ─────────────────────────────────────────────────────

/**
 * Render a small sub-section header (e.g. "Monsters", "Peilbuizen").
 * @param {jsPDF} doc
 * @param {string} title
 * @param {number} y  current Y position
 * @returns {number}  new Y position
 */
function subSectionHeader(doc, title, y) {
  tf(doc, 7, 'bold', [80, 96, 120]);
  doc.text(title, ML, y + 3.5);
  doc.setDrawColor(200, 210, 222); doc.setLineWidth(0.2);
  doc.line(ML, y + 5, ML + CW, y + 5);
  return y + 7;
}

/**
 * Render a compact data table for monsters or peilbuizen.
 * @param {jsPDF} doc
 * @param {string[]} headers
 * @param {string[][]} rows
 * @param {number[]} colWidths  must sum to CW
 * @param {number} startY
 * @param {(y: number) => void} needFn  page-break callback
 * @returns {number}  new Y position
 */
function miniTable(doc, headers, rows, colWidths, startY, needFn) {
  let y = startY;
  const ROW_H = 5.5;
  const CELL_PAD = 1.5;

  // Header row
  needFn(ROW_H + 2);
  doc.setFillColor(238, 242, 248);
  doc.rect(ML, y, CW, ROW_H, 'F');
  tf(doc, 6.5, 'bold', [60, 76, 100]);
  let cx = ML;
  headers.forEach((h, i) => {
    doc.text(h, cx + CELL_PAD, y + 3.8);
    cx += colWidths[i];
  });
  doc.setDrawColor(195, 205, 220); doc.setLineWidth(0.18);
  doc.line(ML, y + ROW_H, ML + CW, y + ROW_H);
  y += ROW_H;

  rows.forEach((row, ri) => {
    needFn(ROW_H);
    if (ri % 2 === 1) {
      doc.setFillColor(248, 250, 253);
      doc.rect(ML, y, CW, ROW_H, 'F');
    }
    tf(doc, 6.5, 'normal', [30, 38, 52]);
    cx = ML;
    row.forEach((cell, i) => {
      const maxW = colWidths[i] - CELL_PAD * 2;
      const text = doc.splitTextToSize(cell || '–', maxW)[0]; // single line clipped
      doc.text(text, cx + CELL_PAD, y + 3.8);
      cx += colWidths[i];
    });
    doc.setDrawColor(218, 224, 234); doc.setLineWidth(0.12);
    doc.line(ML, y + ROW_H, ML + CW, y + ROW_H);
    y += ROW_H;
  });

  // Bottom border
  doc.setDrawColor(175, 188, 205); doc.setLineWidth(0.22);
  doc.line(ML, y, ML + CW, y);

  return y + 4;
}

// ─── Main ────────────────────────────────────────────────────────────────────
/**
 * @param {import('@mv/models').ProjectModel} projectModel
 * @param {import('@mv/models').Boring[]} rawBorings  flat boring objects with .peilbuizen[]
 * @param {string} filename
 */
/** Bouwt het volledige project-rapport als jsPDF-doc (zonder op te slaan) — zodat de
 *  Export-tab het bestand ook als voorvertoning (via pdf.js) kan renderen. */
export async function buildProjectReportDoc(projectModel, rawBorings = [], opts = {}) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const borings = projectModel.borings || projectModel.meetpunten || [];
  if (!borings.length) {
    doc.setFontSize(10); doc.text('Geen boringen gevonden.', ML, MT + 10);
    return doc;
  }

  // Build lookup: boring guid/name → raw boring (for peilbuizen)
  const rawByGuid = new Map();
  const rawByName = new Map();
  for (const rb of rawBorings) {
    if (rb.id)   rawByGuid.set(rb.id, rb);
    if (rb.name) rawByName.set(rb.name.toLowerCase().trim(), rb);
  }

  let y = MT + 2;

  function need(h) {
    if (y + h > PH - MB - 10) { doc.addPage(); y = MT + 2; }
  }

  for (const mp of borings) {
    const layers = mp.layers || [];

    // Look up raw boring (peilbuizen/afwerking + het nieuwe boorprofiel).
    const rawBoring = rawByGuid.get(mp.guid) || rawByName.get((mp.name || '').toLowerCase().trim()) || null;
    const peilbuizen = rawBoring?.peilbuizen || [];
    const monsters = mp.monsters || [];

    // ── Boorprofiel: de nieuwe SVG-weergave (gerasterd), i.p.v. de oude handgetekende
    //    profielkolom. De SVG bevat de kop (naam/maaiveld/boormeester), het profiel, de
    //    laagbeschrijving en de legenda — dus vervangt ook de losse titel/meta.
    let placedProfile = false;
    if (layers.length && rawBoring) {
      try {
        const d = buildBoorprofielData(rawBoring, {});
        if (d) {
          // Precies zoals in de boorprofielen-export: volle breedte, eigen legenda rechts, zelfde
          // standaardschaal (zie boorprofiel-layout.js).
          const { scaled, height } = scaleForBlock(d, { maxHeightMm: PH - MB - 10 - (MT + 2) });
          need(height);
          await drawScaledProfile(doc, scaled, ML, y);
          y += height + 4;
          placedProfile = true;
        }
      } catch (_) { /* val terug op een tekstkop hieronder */ }
    }
    if (!placedProfile) {
      need(16);
      tf(doc, 10, 'bold', [22, 48, 90]);
      doc.text(mp.name || 'Naamloos', ML, y + 5.5);
      y += 10;
      tf(doc, 7.5, 'italic', [165, 170, 180]);
      doc.text('Geen lagen beschikbaar.', ML, y + 2);
      y += 8;
    }

    // ── Monsters ──────────────────────────────────────────────────────────
    if (monsters.length > 0) {
      need(10 + monsters.length * 5.5 + 4);
      y = subSectionHeader(doc, 'Monsters', y);
      const monsterRows = monsters.map(m => [
        m.name || '–',
        m.identificatie || '–',
        m.upperDepth != null && m.lowerDepth != null ? `${m.upperDepth}–${m.lowerDepth} cm` : '–',
        m.type || '–',
      ]);
      y = miniTable(doc, ['Naam', 'Barcode', 'Diepte', 'Type'], monsterRows,
        [35, 50, 30, CW - 115], y, need);
    }

    // ── Peilbuizen ────────────────────────────────────────────────────────
    if (peilbuizen.length > 0) {
      need(10 + peilbuizen.length * 5.5 + 4);
      y = subSectionHeader(doc, 'Peilbuizen', y);
      const pbRows = peilbuizen.map(pb => [
        pb.name || '–',
        pb.filterTopCm != null && pb.filterBottomCm != null ? `${pb.filterTopCm}–${pb.filterBottomCm} cm` : '–',
        pb.diameter != null ? `${pb.diameter} mm` : '–',
        pb.material || '–',
        pb.flowType || '–',
        pb.remark || '–',
      ]);
      y = miniTable(doc, ['Naam', 'Filter', 'Ø', 'Materiaal', 'Doorloop', 'Opmerking'], pbRows,
        [25, 28, 18, 28, 22, CW - 121], y, need);
    }

    y += 10;
  }

  // ── Stamp chrome ─────────────────────────────────────────────────────────
  const logo = await loadCompanyOrBrandLogo(opts.logo);
  const total = doc.internal.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    stampHeader(doc, projectModel, logo);
    stampFooter(doc, p, total);
  }

  return doc;
}

/** Bouwt het rapport en downloadt het. */
export async function generateProjectPDF(projectModel, rawBorings = [], filename = 'project-rapport.pdf', opts = {}) {
  const doc = await buildProjectReportDoc(projectModel, rawBorings, opts);
  doc.save(filename);
}
